// src/comps/MyHeader.js
import React, { useEffect, useState, useCallback } from 'react';
import "../models/LabReport.css";
import AppConst from "../apis/appconst";
import axios from "axios";
import { toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import ChatWidget from "./ChatWidget";

function MyHeader() {
  const [ldata, setLdata] = useState(null);
  const [hospitalName, setHospitalName] = useState("");
  const [aiOpen, setAiOpen] = useState(false);

  const initLdata = () => {
    const storedData = sessionStorage.getItem("ldata");
    if (storedData) {
      try {
        const parsed = JSON.parse(storedData);
        setLdata(parsed);
      } catch (e) {
        console.warn("Failed to parse ldata from sessionStorage", e);
      }
    }
  };

  const sanitize = (text) => {
    if (!text && text !== "") return "";
    return String(text).replace(/hmanager/gi, "").replace(/\s{2,}/g, " ").trim();
  };

  const fetchHospitalProfile = async () => {
    try {
      const storedData = sessionStorage.getItem("ldata");
      if (!storedData) return;
      const fd = new FormData();
      fd.append('profile_hosp_user', 1);
      const response = await axios.post(AppConst.BASE_URL + AppConst.getProfileUrl1, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (response?.data?.status) {
        const user_dt = response.data.user_dt;
        if (user_dt && user_dt.profile_hosp_name) {
          setHospitalName(sanitize(user_dt.profile_hosp_name));
        } else {
          setHospitalName("");
        }
      }
    } catch (err) {
      console.error("Error fetching hospital profile:", err);
    }
  };

  useEffect(() => {
    initLdata();
    fetchHospitalProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // prevent body scroll when AI modal open
  useEffect(() => {
    if (aiOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [aiOpen]);

  // close AI on Escape
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && aiOpen) setAiOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [aiOpen]);

  const computeDuration = (text) => {
    const len = (text || "").trim().length || 10;
    const duration = Math.max(3, 3 + len * 0.12);
    return `${duration}s`;
  };

  const repeatedText = `${hospitalName || "Hospital Name"} \u00A0 \u2022 \u00A0 `;
  const displayUserName = sanitize(ldata?.fname || "");

  /**
   * Check for SW update and activate it if waiting.
   * Returns true if an update activation flow was started (will reload via controllerchange),
   * returns false if no update was found (caller may perform a normal page reload).
   */
  const checkAndActivateUpdate = useCallback(async () => {
    if (!('serviceWorker' in navigator)) {
      console.warn("[Header] Service Worker not supported in this browser");
      toast.error("Service Worker not supported in this browser");
      return false;
    }

    try {
      console.log("[Header] Checking for SW registration...");
      const registration = await navigator.serviceWorker.getRegistration();
      console.log("[Header] registration:", registration);

      if (!registration) {
        toast.info("No service worker registered.");
        console.info("[Header] No service worker registration found.");
        return false;
      }

      // Ask the browser to check the server for a newer SW file
      try {
        console.log("[Header] Calling registration.update()");
        await registration.update();
      } catch (upErr) {
        console.warn("[Header] registration.update() failed:", upErr);
      }

      // If a new worker is waiting, tell it to skipWaiting and wait for controllerchange to reload
      if (registration.waiting) {
        console.log("[Header] Found waiting worker — will request skipWaiting and reload.");
        toast.info("Update found — activating now...");

        const waitingWorker = registration.waiting;

        const onControllerChange = () => {
          console.log("[Header] controllerchange fired — reloading page.");
          // reload once new controller takes over
          window.location.reload();
        };

        // Use controllerchange to reload once the new SW claims clients
        navigator.serviceWorker.addEventListener("controllerchange", onControllerChange);

        try {
          waitingWorker.postMessage({ type: "SKIP_WAITING" });
        } catch (err) {
          console.warn("[Header] postMessage to waiting worker failed:", err);
          // fallback to reload if postMessage fails
          window.location.reload();
        }

        // safety cleanup: remove listener after a bit
        setTimeout(() => {
          try {
            navigator.serviceWorker.removeEventListener("controllerchange", onControllerChange);
          } catch (e) {}
        }, 10000);

        return true; // update activation started
      }

      // No waiting worker
      console.info("[Header] No waiting worker present after update()");
      return false;
    } catch (err) {
      console.error("[Header] Error while checking for update:", err);
      toast.error("Error while checking for update (see console).");
      return false;
    }
  }, []);

  // Handler for refresh button: try SW update first, otherwise just reload page.
  const handleRefreshClick = async (e) => {
    e && e.preventDefault();
    // If offline, just inform user and do not attempt reload
    if (!navigator.onLine) {
      toast.error("You are offline — cannot refresh.");
      return;
    }

    // Try to activate SW update. If none, do a normal reload.
    const didStartUpdate = await checkAndActivateUpdate();
    if (!didStartUpdate) {
      toast.info("Refreshing page...");
      // Force reload from server (not relying on cached service worker)
      // Adding a cache-busting query param ensures a fresh fetch.
      const url = new URL(window.location.href);
      url.searchParams.set('_r', String(Date.now()));
      window.location.href = url.toString();
    }
  };

  return (
    <>
      <header id="page-topbar" className="header-lightblue">
        <div className="layout-width header-fullwidth">
          <div className="navbar-header d-flex align-items-center justify-content-between">

            {/* LEFT: AI button (before phone) */}
            <div className="d-flex align-items-center">
              <button
                type="button"
                className="btn btn-outline-success btn-sm me-2"
                onClick={() => setAiOpen(true)}
                title="Open AI Assistant"
                aria-label="Open AI Assistant"
              >
                <span aria-hidden>🤖</span>
                <span className="d-none d-md-inline ms-1">AI</span>
              </button>

              {/* Refresh / Check button placed BEFORE phone number */}
              <button
                type="button"
                className="btn btn-outline-primary btn-sm me-2 d-flex align-items-center"
                onClick={handleRefreshClick}
                title="Check for updates and refresh page"
                aria-label="Check for updates and refresh page"
                style={{ fontWeight: 600 }}
              >
                <span className="me-1" aria-hidden>🔄</span>
                <span className="d-none d-md-inline">Refresh</span>
              </button>

              {/* Phone moved to left side — visible only on desktop (lg and up) */}
              <div className="service-call ms-0 d-none d-lg-flex align-items-center">
                <i className="ri-phone-fill me-1 text-success" />
                <a href="tel:+919126538413" className="fw-semibold text-dark">
                  +91 9126538413
                </a>
              </div>
            </div>

            {/* Mobile offcanvas toggler - visible only on small screens */}
            <button
              className="btn btn-sm d-lg-none me-2"
              type="button"
              aria-controls="mobileOffcanvas"
              aria-label="Open menu"
              onClick={() => {
                const openOffcanvas = () => {
                  const offcanvasEl = document.getElementById("mobileOffcanvas");
                  if (!offcanvasEl) return null;
                  const BsOff = window.bootstrap?.Offcanvas;
                  const instance = (BsOff && (BsOff.getInstance(offcanvasEl))) || (BsOff ? new BsOff(offcanvasEl) : null);
                  if (instance && typeof instance.show === "function") {
                    instance.show();
                    return true;
                  }
                  offcanvasEl.classList.add("show");
                  document.body.classList.add("offcanvas-open");
                  return true;
                };

                let tries = 0;
                const tryOpen = () => {
                  tries++;
                  if (openOffcanvas()) return;
                  if (tries > 10) {
                    console.warn("mobileOffcanvas not mounted after retries");
                    return;
                  }
                  setTimeout(tryOpen, 50);
                };
                tryOpen();
              }}
            >
              <i className="ri-menu-line" />
            </button>

            {/* CENTER: Full-width sliding hospital name */}
            <div className="header-middle flex-grow-1 text-center overflow-hidden">
              <div
                className="hospital-marquee"
                aria-hidden={hospitalName ? "false" : "true"}
                role="marquee"
              >
                <div
                  className="marquee__inner"
                  style={{ animationDuration: computeDuration(hospitalName) }}
                >
                  <span className="marquee-text">{repeatedText}</span>
                  <span className="marquee-text">{repeatedText}</span>
                  <span className="marquee-text">{repeatedText}</span>
                </div>
              </div>
            </div>

            {/* RIGHT: User info */}
            <div className="d-flex align-items-center">
              <div className="topbar-user ms-3">
                <button type="button" className="btn material-shadow-none user-btn" id="page-header-user-dropdown">
                  <span className="d-flex align-items-center">
                    <span className="text-start ms-1">
                      <span className="d-none d-xl-inline-block ms-1 fw-medium user-name-text">
                        {displayUserName}
                      </span>
                      <br />
                      <span className="d-none d-xl-block ms-1 fs-12 user-name-sub-text">
                        {sanitize(ldata?.email || "")}
                      </span>
                    </span>
                  </span>
                </button>
              </div>
            </div>

          </div>
        </div>
      </header>

      {/* Chat widget modal */}
      <ChatWidget
        open={aiOpen}
        onClose={() => setAiOpen(false)}
        // API path on your CI backend (ensure route /ai/chat exists)
        apiUrl="/ai/chat"
        // pass user/session info if needed for backend auth
        user={ldata}
      />
    </>
  );
}

export default MyHeader;
